import React, { useCallback, useEffect, useState } from 'react';
import { sb } from '../../supabase';
import {
  amIExclusiveSeller, categoryName, deleteExclusiveCategory, fetchMyExclusiveCategories, fetchMyProductCategories,
  LANGS, reorderExclusiveCategories, saveExclusiveCategory, setExclusiveProductCategories, translateCategoryName,
} from '../../lib/marketExclusives';

// «فئات الحصريات» on a «حصريات معبر» seller's home (owner, 2026-10-29): the
// seller makes their own categories, named in whatever language they write —
// the other two are translated (the chat's translation) and shown for
// correction before saving — orders them, and puts each product in one. The
// store's exclusives page shows the categories that have products. A product
// can be in several categories (tap to add or remove, 2026-10-29). Renders
// nothing for every other supplier. Every write is an RPC that checks the
// seller and ownership on the server.

const T = {
  ar: {
    title: 'فئات الحصريات', sub: 'اكتب اسم الفئة بأي لغة، ونترجمه للغتين الثانيتين. الفئات اللي فيها منتجات تظهر للتجار في صفحة «حصريات معبر».',
    name: 'اسم الفئة', translate: 'ترجم', translating: 'جارٍ الترجمة…', save: 'احفظ الفئة', cancel: 'إلغاء', edit: 'تعديل', del: 'حذف',
    up: 'أعلى', down: 'أسفل', none: 'لا توجد فئات بعد.', products: 'منتجاتك', noCat: 'بدون فئة', noProducts: 'لا توجد منتجات في حسابك.',
    confirmDel: 'تحذف هذه الفئة؟ منتجاتها تبقى في صفحة الحصريات بدون فئة.', saved: 'حُفظ', failed: 'تعذّر الحفظ', failedTr: 'تعذّرت ترجمة: ',
    lang: { ar: 'عربي', en: 'English', zh: '中文' }, hidden: 'غير ظاهر', live: 'ظاهر',
  },
  en: {
    title: 'Exclusives categories', sub: 'Type a category name in any language — we translate it into the other two. Categories with products show to traders on the «MAABAR Exclusives» page.',
    name: 'Category name', translate: 'Translate', translating: 'Translating…', save: 'Save category', cancel: 'Cancel', edit: 'Edit', del: 'Delete',
    up: 'Up', down: 'Down', none: 'No categories yet.', products: 'Your products', noCat: 'No category', noProducts: 'No products in your account.',
    confirmDel: 'Delete this category? Its products stay on the exclusives page without a category.', saved: 'Saved', failed: 'Could not save', failedTr: 'Could not translate: ',
    lang: { ar: 'عربي', en: 'English', zh: '中文' }, hidden: 'not shown', live: 'shown',
  },
  zh: {
    title: '独家分类', sub: '用任意语言输入分类名称，我们会翻译成另外两种语言。有产品的分类会在「MAABAR 独家」页面向买家显示。',
    name: '分类名称', translate: '翻译', translating: '翻译中…', save: '保存分类', cancel: '取消', edit: '编辑', del: '删除',
    up: '上移', down: '下移', none: '暂无分类。', products: '您的产品', noCat: '无分类', noProducts: '您的账户中没有产品。',
    confirmDel: '删除此分类？其产品仍会显示在独家页面，但没有分类。', saved: '已保存', failed: '保存失败', failedTr: '无法翻译：',
    lang: { ar: 'عربي', en: 'English', zh: '中文' }, hidden: '未显示', live: '已显示',
  },
};

export default function ExclusiveCategoriesPanel({ lang = 'ar', supplierId }) {
  const t = T[lang] || T.ar;
  const font = lang === 'ar' ? 'var(--font-ar)' : 'var(--font-sans)';
  const [on, setOn] = useState(false);
  const [cats, setCats] = useState([]);
  const [map, setMap] = useState({});
  const [products, setProducts] = useState([]);
  const [typed, setTyped] = useState('');
  const [draft, setDraft] = useState(null);     // { id, ar, en, zh } being added or edited
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2600); };

  const load = useCallback(async () => {
    const [c, m, p] = await Promise.all([
      fetchMyExclusiveCategories(),
      fetchMyProductCategories(),
      sb.from('products').select('id, name_ar, name_en, name_zh, is_active, is_draft').eq('supplier_id', supplierId).order('created_at', { ascending: false }),
    ]);
    setCats(c); setMap(m); setProducts(p.data || []);
  }, [supplierId]);
  useEffect(() => {
    let alive = true;
    amIExclusiveSeller().then((v) => { if (!alive) return; setOn(v); if (v) load(); });
    return () => { alive = false; };
  }, [load]);
  if (!on) return null;

  const translate = async () => {
    setBusy('tr');
    const r = await translateCategoryName(typed);
    setBusy('');
    setDraft({ id: null, ar: r.ar, en: r.en, zh: r.zh });
    if (r.failed.length) flash(t.failedTr + r.failed.map((l) => t.lang[l]).join('، '));
  };
  const save = async () => {
    setBusy('save');
    const r = await saveExclusiveCategory(draft);
    setBusy('');
    if (!r.ok) { flash(`${t.failed}: ${r.error}`); return; }
    setDraft(null); setTyped(''); flash(t.saved); load();
  };
  const remove = async (c) => {
    if (!window.confirm(t.confirmDel)) return;
    const r = await deleteExclusiveCategory(c.id);
    if (!r.ok) { flash(`${t.failed}: ${r.error}`); return; }
    load();
  };
  const move = async (i, d) => {
    const ids = cats.map((c) => c.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setCats(ids.map((id) => cats.find((c) => c.id === id)));
    const r = await reorderExclusiveCategories(ids);
    if (!r.ok) { flash(`${t.failed}: ${r.error}`); load(); }
  };
  // Tap a category to put the product in it, tap again to take it out.
  const toggle = async (productId, categoryId) => {
    const cur = map[productId] || [];
    const next = cur.includes(categoryId) ? cur.filter((x) => x !== categoryId) : [...cur, categoryId];
    setMap((m) => ({ ...m, [productId]: next }));
    const r = await setExclusiveProductCategories(productId, next);
    if (!r.ok) { flash(`${t.failed}: ${r.error}`); load(); }
  };

  const card = { background: '#fff', border: '1px solid var(--border-subtle, #E8E5DE)', borderRadius: 16, padding: '16px 18px', marginBottom: 16, fontFamily: font };
  const input = { height: 36, padding: '0 10px', border: '1px solid var(--border, #D9D2C4)', borderRadius: 8, fontSize: 13.5, minWidth: 0, fontFamily: font };
  const btn = (primary) => ({ height: 34, padding: '0 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: primary ? 'none' : '1px solid var(--border, #D9D2C4)', background: primary ? '#1A1814' : '#fff', color: primary ? '#fff' : '#1A1814', fontFamily: font });
  const pname = (p) => p[`name_${lang}`] || p.name_ar || p.name_en || p.name_zh || p.id.slice(0, 8);

  return (
    <div data-exclusive-categories-panel="1" style={card}>
      <p style={{ margin: 0, fontSize: 15.5, fontWeight: 700 }}>{t.title}</p>
      <p style={{ margin: '4px 0 12px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{t.sub}</p>

      {!draft ? (
        <form onSubmit={(e) => { e.preventDefault(); if (typed.trim()) translate(); }} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t.name} maxLength={60} dir="auto" data-cat-typed="1" style={{ ...input, flex: '1 1 220px' }} />
          <button type="submit" disabled={!typed.trim() || busy === 'tr'} style={btn(true)} data-cat-translate="1">{busy === 'tr' ? t.translating : t.translate}</button>
        </form>
      ) : (
        <div data-cat-draft="1" style={{ display: 'grid', gap: 8, padding: 12, borderRadius: 12, background: 'var(--bg-subtle, #F7F4EE)' }}>
          {LANGS.map((l) => (
            <label key={l} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
              <span style={{ width: 64, color: 'var(--text-secondary)' }}>{t.lang[l]}</span>
              <input value={draft[l]} onChange={(e) => setDraft({ ...draft, [l]: e.target.value })} maxLength={60} dir={l === 'ar' ? 'rtl' : 'ltr'} data-cat-name={l} style={{ ...input, flex: 1 }} />
            </label>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={save} disabled={busy === 'save' || !LANGS.some((l) => draft[l].trim())} style={btn(true)} data-cat-save="1">{t.save}</button>
            <button type="button" onClick={() => setDraft(null)} style={btn(false)}>{t.cancel}</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {cats.length === 0 ? <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>{t.none}</p> : cats.map((c, i) => {
          const n = products.filter((p) => (map[p.id] || []).includes(c.id)).length;
          return (
            <div key={c.id} data-cat-row={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 0', borderTop: i ? '1px solid var(--border-subtle, #EEE)' : 'none' }}>
              <span style={{ flex: '1 1 200px', minWidth: 0 }}>
                <b style={{ fontSize: 13.5 }}>{categoryName(c, lang)}</b>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)' }} dir="auto">{LANGS.filter((l) => l !== lang && c[`name_${l}`]).map((l) => c[`name_${l}`]).join(' · ')} · {n}</span>
              </span>
              <button type="button" style={btn(false)} disabled={i === 0} onClick={() => move(i, -1)} aria-label={t.up}>↑</button>
              <button type="button" style={btn(false)} disabled={i === cats.length - 1} onClick={() => move(i, 1)} aria-label={t.down}>↓</button>
              <button type="button" style={btn(false)} onClick={() => setDraft({ id: c.id, ar: c.name_ar || '', en: c.name_en || '', zh: c.name_zh || '' })}>{t.edit}</button>
              <button type="button" style={{ ...btn(false), color: '#B4372C' }} onClick={() => remove(c)} data-cat-delete={c.id}>{t.del}</button>
            </div>
          );
        })}
      </div>

      {cats.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700 }}>{t.products}</p>
          {products.length === 0 ? <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>{t.noProducts}</p> : products.map((p) => {
            const live = p.is_active === true && p.is_draft !== true;
            return (
              <div key={p.id} data-cat-product={p.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border-subtle, #EEE)' }}>
                <span style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pname(p)} <span style={{ fontSize: 11, color: live ? '#2D6A4F' : 'var(--text-secondary)' }}>· {live ? t.live : t.hidden}</span>
                  {(map[p.id] || []).length === 0 && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}> · {t.noCat}</span>}
                </span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {cats.map((c) => {
                    const on = (map[p.id] || []).includes(c.id);
                    return (
                      <button key={c.id} type="button" aria-pressed={on} onClick={() => toggle(p.id, c.id)} data-cat-toggle={`${p.id}:${c.id}`}
                        style={{ height: 30, padding: '0 12px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer', fontFamily: font,
                          border: `1px solid ${on ? '#1A1814' : 'var(--border, #D9D2C4)'}`, background: on ? '#1A1814' : '#fff', color: on ? '#fff' : '#1A1814', fontWeight: on ? 700 : 400 }}>
                        {on ? '✓ ' : ''}{categoryName(c, lang)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {msg && <p data-cat-msg="1" style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>{msg}</p>}
    </div>
  );
}
